import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    CourseInsightDetail,
    CourseInsightSummary,
    PageResponse,
    fetchCourseInsightDetail,
    fetchCourseInsights,
} from "../services/polarisService";
import { decodeHtmlEntities } from "../utils/htmlUtils";
import "../styles/polaris-admin.css";
import PolarisAdminSelect from "./PolarisAdminSelect";

const PAGE_SIZE = 20;

const TAB_OPTIONS: { value: number; label: string }[] = [
    { value: 3, label: "College Planning" },
    { value: 4, label: "NIL EDU" },
    { value: 5, label: "Connect" },
    { value: 6, label: "DiscoverU" },
    { value: 7, label: "Spotlight" },
    { value: 10, label: "Resources" },
    { value: 11, label: "Tutoring" },
    { value: 12, label: "Advertising" },
];

const EVENT_TYPE_LABELS: Record<string, string> = {
    VIDEO_OPEN: "Video opened",
    VIDEO_COMPLETE: "Video completed",
    LINK_CLICK: "Link clicked",
    PDF_DOWNLOAD: "PDF downloaded",
};

function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function contentTypeLabel(contentType: string | null | undefined): string {
    if (!contentType) return "—";
    const lower = contentType.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function eventTypeLabel(eventType: string): string {
    return EVENT_TYPE_LABELS[eventType] ?? eventType;
}

function getPageNumbers(current: number, total: number): (number | "ellipsis")[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    const pages: (number | "ellipsis")[] = [];
    pages.push(0);
    if (current > 3) pages.push("ellipsis");
    for (let i = Math.max(1, current - 2); i <= Math.min(total - 2, current + 2); i++) {
        pages.push(i);
    }
    if (current < total - 4) pages.push("ellipsis");
    pages.push(total - 1);
    return pages;
}

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalElements: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    itemLabel?: string;
}

function Pagination({ currentPage, totalPages, totalElements, pageSize, onPageChange, itemLabel = "items" }: PaginationProps) {
    if (totalPages <= 1) return null;
    const start = currentPage * pageSize + 1;
    const end = Math.min(currentPage * pageSize + pageSize, totalElements);
    const pages = getPageNumbers(currentPage, totalPages);

    return (
        <div className="pa-pagination">
            <div className="pa-pagination-info">
                Showing <strong>{start}–{end}</strong> of <strong>{totalElements}</strong> {itemLabel}
            </div>
            <div className="pa-pagination-controls">
                <button className="pa-page-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0} aria-label="Previous page">Previous</button>
                {pages.map((p, i) =>
                    p === "ellipsis" ? (
                        <span key={`e-${i}`} className="pa-page-ellipsis">…</span>
                    ) : (
                        <button key={p} className={`pa-page-btn${p === currentPage ? " active" : ""}`} onClick={() => onPageChange(p as number)} aria-label={`Page ${(p as number) + 1}`} aria-current={p === currentPage ? "page" : undefined}>
                            {(p as number) + 1}
                        </button>
                    )
                )}
                <button className="pa-page-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} aria-label="Next page">Next</button>
            </div>
        </div>
    );
}

type SortKey = "title" | "tabLabel" | "moduleCount" | "uniqueUsers" | "totalEvents" | "lastActivity";

const COLUMN_COUNT = 6;

function ModuleDetail({ detail, loading, error }: { detail: CourseInsightDetail | null; loading: boolean; error: string | null }) {
    if (loading) return <div className="pa-loading">Loading activity…</div>;
    if (error) return <div className="pa-error">{error}</div>;
    if (!detail) return null;
    if (detail.modules.length === 0) {
        return <div className="pa-drill-empty">No modules or recorded activity for this course yet.</div>;
    }
    return (
        <div className="pa-drill">
            {detail.modules.map((module, idx) => (
                <div className="pa-drill-module" key={module.moduleId ?? `course-${idx}`}>
                    <div className="pa-drill-module-header">
                        <span className="pa-drill-module-title">{decodeHtmlEntities(module.title || "Untitled")}</span>
                        {module.contentType && (
                            <span className="pa-badge pa-badge-trialing">{contentTypeLabel(module.contentType)}</span>
                        )}
                        <span className="pa-drill-module-stats">
                            {module.uniqueUsers} {module.uniqueUsers === 1 ? "viewer" : "viewers"} · {module.totalEvents} {module.totalEvents === 1 ? "event" : "events"}
                        </span>
                    </div>
                    {module.events.length === 0 ? (
                        <div className="pa-drill-empty">No activity recorded yet.</div>
                    ) : (
                        <table className="pa-table pa-drill-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Company / School</th>
                                    <th>Interaction</th>
                                    <th>When</th>
                                </tr>
                            </thead>
                            <tbody>
                                {module.events.map((event, eIdx) => (
                                    <tr key={`${event.userId ?? "u"}-${eIdx}`}>
                                        <td>{event.name || "—"}</td>
                                        <td>{event.email || "—"}</td>
                                        <td>{event.companyName ?? "—"}</td>
                                        <td>{eventTypeLabel(event.eventType)}</td>
                                        <td>{formatDateTime(event.occurredAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function PolarisCourseInsightsPage() {
    const [data, setData] = useState<PageResponse<CourseInsightSummary> | null>(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [tabFilter, setTabFilter] = useState<number | "">("");
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState<SortKey>("lastActivity");
    const [sortAsc, setSortAsc] = useState(false);

    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [detailCache, setDetailCache] = useState<Record<number, CourseInsightDetail>>({});
    const [detailLoadingId, setDetailLoadingId] = useState<number | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);

    useEffect(() => {
        const handle = window.setTimeout(() => setSearch(searchInput.trim()), 300);
        return () => window.clearTimeout(handle);
    }, [searchInput]);

    const load = useCallback(async (p: number, isInitial: boolean) => {
        if (isInitial) setLoading(true); else setPaging(true);
        setError(null);
        try {
            const result = await fetchCourseInsights(p, PAGE_SIZE, tabFilter === "" ? undefined : tabFilter, search || undefined);
            setData(result);
        } catch (e: any) {
            setError(e.message ?? "Failed to load course insights");
        } finally {
            setLoading(false);
            setPaging(false);
        }
    }, [tabFilter, search]);

    const isInitialLoad = useRef(true);
    useEffect(() => {
        const initial = isInitialLoad.current;
        isInitialLoad.current = false;
        load(page, initial);
    }, [page, load]);

    // Reset to the first page and collapse any open row when filters change.
    useEffect(() => {
        setPage(0);
        setExpandedId(null);
    }, [tabFilter, search]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) setSortAsc((a) => !a);
        else { setSortKey(key); setSortAsc(true); }
    };

    const sorted = useMemo(() => {
        if (!data) return [];
        return [...data.content].sort((a, b) => {
            const av = a[sortKey] ?? "";
            const bv = b[sortKey] ?? "";
            const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
            return sortAsc ? cmp : -cmp;
        });
    }, [data, sortKey, sortAsc]);

    const toggleRow = useCallback(async (row: CourseInsightSummary) => {
        if (expandedId === row.courseId) {
            setExpandedId(null);
            return;
        }
        setExpandedId(row.courseId);
        setDetailError(null);
        if (detailCache[row.courseId]) return;
        setDetailLoadingId(row.courseId);
        try {
            const detail = await fetchCourseInsightDetail(row.courseId, row.tab);
            setDetailCache((prev) => ({ ...prev, [row.courseId]: detail }));
        } catch (e: any) {
            setDetailError(e.message ?? "Failed to load activity");
        } finally {
            setDetailLoadingId(null);
        }
    }, [expandedId, detailCache]);

    const SortTh = ({ col, label }: { col: SortKey; label: string }) => (
        <th className="sortable" onClick={() => handleSort(col)}>
            {label}<span className="pa-sort-indicator">{sortKey === col ? (sortAsc ? " ▲" : " ▼") : " ⇅"}</span>
        </th>
    );

    return (
        <section className="pa-section" data-testid="polaris-course-insights-panel">
            <p className="pa-section-subtitle">Who watched or opened each course and module, when, and how often.</p>

            <div className="pa-toolbar">
                <input
                    className="pa-filter-input pa-toolbar-search"
                    placeholder="Search by course title…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    aria-label="Search course insights"
                />
                <PolarisAdminSelect
                    size="compact"
                    className="pa-toolbar-select"
                    value={tabFilter === "" ? "" : String(tabFilter)}
                    options={[
                        { value: "", label: "All content areas" },
                        ...TAB_OPTIONS.map((t) => ({
                            value: String(t.value),
                            label: t.label,
                        })),
                    ]}
                    onChange={(next) => setTabFilter(next === "" ? "" : Number(next))}
                    data-testid="polaris-course-insights-tab-filter"
                />
            </div>

            {error && <div className="pa-error">{error}</div>}
            {loading && <div className="pa-loading">Loading course insights…</div>}
            {!loading && !error && (
                <div className="pa-table-wrapper" style={{ opacity: paging ? 0.5 : 1, transition: "opacity 0.15s ease" }}>
                    <table className="pa-table">
                        <thead>
                            <tr>
                                <th aria-label="Expand" style={{ width: "32px" }} />
                                <SortTh col="title" label="Course / Content" />
                                <SortTh col="tabLabel" label="Area" />
                                <SortTh col="moduleCount" label="Modules" />
                                <SortTh col="uniqueUsers" label="Viewers" />
                                <SortTh col="totalEvents" label="Events" />
                                <SortTh col="lastActivity" label="Last Activity" />
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 ? (
                                <tr><td colSpan={COLUMN_COUNT + 1} style={{ textAlign: "center", padding: "24px", color: "var(--muted)" }}>No courses found</td></tr>
                            ) : (
                                sorted.map((row) => {
                                    const isExpanded = expandedId === row.courseId;
                                    return (
                                        <React.Fragment key={`${row.tab}-${row.courseId}`}>
                                            <tr
                                                className={`pa-expandable-row${isExpanded ? " is-expanded" : ""}`}
                                                onClick={() => toggleRow(row)}
                                            >
                                                <td>
                                                    <span className={`pa-expand-chevron${isExpanded ? " open" : ""}`} aria-hidden>▶</span>
                                                </td>
                                                <td>{decodeHtmlEntities(row.title || "Untitled")}</td>
                                                <td><span className="pa-badge pa-badge-inactive">{row.tabLabel}</span></td>
                                                <td>{row.moduleCount}</td>
                                                <td>{row.uniqueUsers}</td>
                                                <td>{row.totalEvents}</td>
                                                <td>{formatDateTime(row.lastActivity)}</td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="pa-detail-row">
                                                    <td colSpan={COLUMN_COUNT + 1}>
                                                        <ModuleDetail
                                                            detail={detailCache[row.courseId] ?? null}
                                                            loading={detailLoadingId === row.courseId}
                                                            error={detailError}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                    {data && (
                        <Pagination
                            currentPage={page}
                            totalPages={data.totalPages}
                            totalElements={data.totalElements}
                            pageSize={PAGE_SIZE}
                            onPageChange={setPage}
                            itemLabel="courses"
                        />
                    )}
                </div>
            )}
        </section>
    );
}
